import { BLEND_MODES, Graphics } from "pixi.js"

// A cheap procedural fire backdrop (one Graphics: a warm glow + a small particle pool, redrawn per
// frame). Intensity (0..1) is the campfire strength: weaker fire => a dimmer glow and fewer, dimmer
// particles. Used behind the ember lord's roster portrait (whose transparent background lets it
// show around the character).

interface Particle {
    x: number
    y: number
    vx: number
    vy: number
    life: number
    maxLife: number
    size: number
}

const MAX_PARTICLES = 30
const SPAWN_RATE = 48 // particles/sec at full intensity

// young (1) -> old (0): near-white core -> yellow -> orange -> deep red. Shared with the in-world
// campfire flame bake (see fire_sprite.ts) so both use the same fire palette.
export const flameColor = (lifeFrac: number): number =>
    lifeFrac > 0.8 ? 0xfff6d0 : lifeFrac > 0.55 ? 0xffd23c : lifeFrac > 0.3 ? 0xff8a1e : 0xe33a12

export class FlameBackdrop extends Graphics {
    private particles: Particle[] = []
    private readonly w: number
    private readonly h: number
    private readonly showGlow: boolean
    private spawnAccumulator = 0
    private _t = 0

    // `glow` draws the layered warm halo behind the particles (the roster card wants it; the in-world
    // campfire flame bakes with glow off so only the particles render — see FireSprite).
    constructor(width: number, height: number, { glow = true }: { glow?: boolean } = {}) {
        super()
        this.w = width
        this.h = height
        this.showGlow = glow
        // Additive blending: warm colours glow against the dark card and overlapping particles /
        // glow accumulate toward white — the classic bright-fire look.
        this.blendMode = BLEND_MODES.ADD
    }

    private spawn = () => {
        const cx = this.w / 2
        this.particles.push({
            x: cx + (Math.random() - 0.5) * this.w * 0.85, // spread across the card, not just centre
            y: this.h - Math.random() * 6,
            vx: (Math.random() - 0.5) * 0.01, // px/ms lateral wobble
            vy: -(0.02 + Math.random() * 0.035), // px/ms upward
            life: 0,
            maxLife: 650 + Math.random() * 800, // ms
            size: 3.5 + Math.random() * 3.5, // px
        })
    }


    update = (dtMs: number, intensity: number) => {
        const dt = Math.min(dtMs, 50) // clamp big frame gaps so particles don't jump
        const i = Math.max(0, Math.min(1, intensity))
        const target = Math.round(MAX_PARTICLES * i)

        // advance + cull
        this.particles = this.particles.filter(p => {
            p.life += dt
            p.x += p.vx * dt
            p.y += p.vy * dt
            return p.life < p.maxLife && p.y > -3
        })

        // spawn toward the target at a rate proportional to intensity
        this.spawnAccumulator += (dt / 1000) * SPAWN_RATE * i
        while (this.spawnAccumulator >= 1) {
            this.spawnAccumulator -= 1
            if (this.particles.length < target) {
                this.spawn()
            }
        }

        // redraw
        this.clear()
        if (i <= 0) {
            return
        }

        // Warm glow rising from the bottom-centre — this is the "backdrop" that dims with weak fire.
        // A gentle flicker keeps it alive even when few particles are around.
        if (this.showGlow) {
            const flicker = 0.85 + 0.15 * Math.sin(this._t + Math.random())
            this._t += 0.15
            const gx = this.w / 2
            const gy = this.h * 0.72
            const glowR = this.w * 0.85
            // Layered warm glow, brightest at the core (additive, so these stack toward white).
            this.beginFill(0xff4a12, 0.3 * i * flicker)
            this.drawEllipse(gx, gy, glowR, glowR * 0.95)
            this.endFill()
            this.beginFill(0xff8a24, 0.32 * i * flicker)
            this.drawEllipse(gx, gy + 3, glowR * 0.62, glowR * 0.74)
            this.endFill()
            this.beginFill(0xffcf5a, 0.34 * i * flicker)
            this.drawEllipse(gx, gy + 6, glowR * 0.34, glowR * 0.5)
            this.endFill()
        }

        // particles
        for (const p of this.particles) {
            const lifeFrac = 1 - p.life / p.maxLife // 1 young -> 0 old
            const alpha = lifeFrac * 0.9 * i
            const r = p.size * (0.5 + 0.5 * lifeFrac)
            this.beginFill(flameColor(lifeFrac), alpha)
            this.drawCircle(p.x, p.y, r)
            this.endFill()
        }
    }
}
